import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import type { ApiError } from '../api';
import { paymentEndpoints } from '../api-payments';
import type { AdminTenderInput, AmountPolicy, IssuancePolicy, PastDuePolicy, WindowCartView } from '../api-payments';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { customerEndpoints } from '../api';
import { CustomerWarnings } from '../components/CustomerRecord';
import { loadStripe } from '../stripe';
import { Badge, Button, ErrorNote, Field, Panel } from '../ui';
import { INPUT, dollars } from './event/shared';
import { LinkFields, linkInput } from './OrderPayment';
import { dateWords } from '../terms';

/**
 * A new order for a customer from Admin (D-71, docs/payment-requests.md §4.1):
 * a phone or group sale. Seats go into the staff member's cart (the box office
 * cart, best available at window prices); then the order is paid in full, a
 * deposit is taken, or nothing is paid yet with a due date, and a payment link
 * can go out at once.
 */

type Payment = 'FULL' | 'DEPOSIT' | 'NONE';
type Kind = 'CHECK' | 'CARD_KEYED' | 'HOUSE_ACCOUNT';

const cents = (text: string): number | null => {
  const value = Number(text.replace(/[$,\s]/g, ''));
  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : null;
};

export default function NewOrderPage() {
  const can = usePermissions();
  if (!can('box_office.sell')) return <NotPermitted permission="box_office.sell" />;
  return <NewOrder />;
}

function NewOrder() {
  const can = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [performanceId, setPerformanceId] = useState('');
  const [ticketTypeId, setTicketTypeId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [quantity, setQuantity] = useState('2');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [delivery, setDelivery] = useState<'MOBILE' | 'EMAIL_PDF' | 'WILL_CALL'>('EMAIL_PDF');
  const [payment, setPayment] = useState<Payment>('DEPOSIT');
  const [kind, setKind] = useState<Kind>('CHECK');
  const [deposit, setDeposit] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [orgId, setOrgId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [issuance, setIssuance] = useState<IssuancePolicy>('ISSUE_WHEN_PAID');
  const [pastDue, setPastDue] = useState<PastDuePolicy>('NOTIFY_STAFF');
  const [sendLink, setSendLink] = useState(true);
  const [link, setLink] = useState({ policy: 'CUSTOM_AMOUNT_ALLOWED' as AmountPolicy, amount: '', minimum: '', expires: '' });
  const [note, setNote] = useState('');
  const [card, setCard] = useState<{ intent_id: string; amount_cents: number; ready: boolean } | null>(null);
  const elementsRef = useRef<{ stripe: Awaited<ReturnType<typeof loadStripe>>; elements: ReturnType<Awaited<ReturnType<typeof loadStripe>>['elements']> } | null>(null);

  const performances = useQuery({ queryKey: ['new-order', 'performances'], queryFn: paymentEndpoints.newPerformances });
  const detail = useQuery({ queryKey: ['new-order', 'performance', performanceId], queryFn: () => paymentEndpoints.performance(performanceId), enabled: Boolean(performanceId) });
  const cart = useQuery<WindowCartView, ApiError>({ queryKey: ['new-order', 'cart'], queryFn: paymentEndpoints.cart });
  const total = cart.data?.totals.customer_total_cents ?? 0;
  /*
   * What staff have written about this person (D-358), asked once the email
   * looks like one. The order is not blocked by it: a do-not-sell is shown with
   * the note that explains it, and the person taking the order decides.
   */
  const typedEmail = email.trim().toLowerCase();
  const known = useQuery({
    queryKey: ['new-order', 'customer-record', typedEmail],
    queryFn: () => customerEndpoints.atSale({ email: typedEmail }),
    enabled: /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(typedEmail) && can('customers.lookup'),
  });
  const setCart = (view: WindowCartView) => queryClient.setQueryData(['new-order', 'cart'], view);

  const hold = useMutation({
    mutationFn: () => {
      const n = Number(quantity);
      if (!performanceId || !ticketTypeId || !Number.isInteger(n) || n < 1) throw new Error(`Choose ${dateWords().a}, a ticket type and how many`);
      return paymentEndpoints.hold({ performance_id: performanceId, ticket_type_id: ticketTypeId, mode: 'best', quantity: n, allow_split: true, ...(zoneId ? { price_zone_ids: [zoneId] } : {}) });
    },
    onSuccess: (r) => {
      setError(null);
      setCart(r.cart);
      setCard(null);
    },
    onError: (cause: Error) => setError(cause.message),
  });
  const clear = useMutation({ mutationFn: paymentEndpoints.clearCart, onSuccess: () => { setCard(null); void cart.refetch(); }, onError: (cause: ApiError) => setError(cause.message) });

  const amountNow = payment === 'FULL' ? total : payment === 'DEPOSIT' ? (cents(deposit) ?? 0) : 0;

  const startCard = async () => {
    setError(null);
    if (amountNow < 50) return setError('Enter the amount to charge first');
    try {
      const started = await paymentEndpoints.startNewCard(amountNow);
      if (!started.client_secret || !started.publishable_key) throw new Error('Stripe did not return a card form');
      const stripe = await loadStripe(started.publishable_key);
      const elements = stripe.elements({ clientSecret: started.client_secret });
      elementsRef.current = { stripe, elements };
      setCard({ intent_id: started.payment_intent_id, amount_cents: amountNow, ready: false });
      window.setTimeout(() => elements.create('payment', { layout: 'tabs' }).mount('#new-order-card'), 0);
    } catch (cause) {
      setError((cause as Error).message);
    }
  };
  const authorize = async () => {
    if (!elementsRef.current || !card) return;
    const { stripe, elements } = elementsRef.current;
    const result = await stripe.confirmPayment({ elements, confirmParams: { return_url: window.location.href }, redirect: 'if_required' });
    if (result.error) return setError(result.error.message ?? 'The card was declined');
    setCard({ ...card, ready: true });
  };

  const create = useMutation({
    mutationFn: () => {
      if (!name.trim() || !email.trim()) throw new Error('The customer’s name and email are required');
      if (total <= 0) throw new Error('Hold seats first');
      const tenders: AdminTenderInput[] = [];
      if (amountNow > 0) {
        if (payment === 'DEPOSIT' && amountNow >= total) throw new Error('A deposit must be less than the total; choose Paid in full instead');
        if (kind === 'CARD_KEYED') {
          if (!card?.ready || card.amount_cents !== amountNow) throw new Error('Authorize the card for this amount first');
          tenders.push({ tender_type: 'CARD_KEYED', amount_cents: amountNow, payment_intent_id: card.intent_id });
        } else if (kind === 'CHECK') {
          tenders.push({ tender_type: 'CHECK', amount_cents: amountNow, check_number: checkNumber.trim() || null });
        } else {
          if (!orgId.trim()) throw new Error('Enter the organization id for the house account');
          tenders.push({ tender_type: 'HOUSE_ACCOUNT', amount_cents: amountNow, organization_id: orgId.trim() });
        }
      } else if (payment === 'DEPOSIT') throw new Error('Enter the deposit amount');
      const owing = payment !== 'FULL';
      const dueAt = dueDate ? new Date(`${dueDate}T23:59:59`).toISOString() : null;
      return paymentEndpoints.createOrder({
        contact: { name: name.trim(), email: email.trim(), phone: phone.trim() || null },
        delivery_method: delivery,
        tenders,
        balance: owing ? { issuance_policy: issuance, due_at: dueAt, past_due_policy: pastDue } : null,
        payment_request: owing && sendLink && can('orders.payment_requests.manage') ? { ...linkInput(link), due_at: dueAt, send: true } : null,
        email_confirmation: true,
        note: note.trim() || null,
      });
    },
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      void navigate(`/orders/${r.order_id}`);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  return (
    <div className="space-y-4">
      <Link to="/orders" className="text-sm text-ink-400 hover:text-ink-900">
        ← Orders
      </Link>
      <h1 className="text-xl font-semibold text-ink-900">New order</h1>
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Panel title="1. Tickets" description="Best available at box office prices. Seats are held for this order while you finish.">
        <div className="space-y-3 px-5 py-4">
          <div className="flex flex-wrap items-end gap-3">
            <Field label={`${dateWords().One}`} htmlFor="no-perf">
              <select id="no-perf" className={INPUT} value={performanceId} onChange={(e) => { setPerformanceId(e.target.value); setTicketTypeId(''); setZoneId(''); }}>
                <option value="">Choose…</option>
                {(performances.data?.performances ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.event_title} · {new Date(p.starts_at).toLocaleString(undefined, { timeZone: p.timezone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · {p.venue_name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Ticket type" htmlFor="no-type">
              <select id="no-type" className={INPUT} value={ticketTypeId} onChange={(e) => setTicketTypeId(e.target.value)} disabled={!detail.data}>
                <option value="">Choose…</option>
                {(detail.data?.ticket_types ?? []).filter((t) => !t.is_comp_type).map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Price zone" htmlFor="no-zone">
              <select id="no-zone" className={INPUT} value={zoneId} onChange={(e) => setZoneId(e.target.value)} disabled={!detail.data}>
                <option value="">Any</option>
                {(detail.data?.zones ?? []).map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.name}
                    {ticketTypeId && detail.data?.prices[z.id]?.[ticketTypeId] ? ` · ${dollars(detail.data.prices[z.id]![ticketTypeId]!.total_cents)}` : ''}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Quantity" htmlFor="no-qty">
              <input id="no-qty" className={`${INPUT} w-20`} value={quantity} onChange={(e) => setQuantity(e.target.value)} inputMode="numeric" />
            </Field>
            <Button variant="primary" disabled={hold.isPending} onClick={() => hold.mutate()}>
              {hold.isPending ? 'Holding…' : 'Add seats'}
            </Button>
          </div>
          {cart.data && cart.data.performances.length > 0 ? (
            <div className="rounded border border-line">
              {cart.data.performances.map((p) => (
                <div key={p.performance_id} className="border-b border-line px-4 py-2 text-sm last:border-0">
                  <p className="font-medium text-ink-900">{p.title} · {new Date(p.starts_at).toLocaleString(undefined, { timeZone: p.timezone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                  <p className="text-xs text-ink-600">{p.seats.map((s) => s.label).join('; ')}</p>
                </div>
              ))}
              <div className="flex items-center justify-between px-4 py-2 text-sm">
                <span>
                  {cart.data.totals.tickets} tickets · fees {dollars(cart.data.totals.fees_cents + cart.data.totals.tax_cents)}
                  {cart.data.expires_at ? <span className="text-xs text-ink-400"> · held until {new Date(cart.data.expires_at).toLocaleTimeString()}</span> : null}
                </span>
                <span className="flex items-center gap-3">
                  <span className="tabular text-base font-semibold">{dollars(total)}</span>
                  <Button variant="danger" disabled={clear.isPending} onClick={() => clear.mutate()}>Release all</Button>
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-ink-400">No seats held yet.</p>
          )}
        </div>
      </Panel>

      <Panel title="2. Customer">
        <div className="flex flex-wrap items-end gap-3 px-5 py-4">
          <Field label="Name" htmlFor="no-name"><input id="no-name" className={INPUT} value={name} onChange={(e) => setName(e.target.value)} /></Field>
          <Field label="Email" htmlFor="no-email"><input id="no-email" type="email" className={INPUT} value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Field label="Phone" htmlFor="no-phone"><input id="no-phone" className={INPUT} value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
          <Field label="Delivery" htmlFor="no-delivery">
            <select id="no-delivery" className={INPUT} value={delivery} onChange={(e) => setDelivery(e.target.value as typeof delivery)}>
              <option value="EMAIL_PDF">Email (PDF)</option>
              <option value="MOBILE">Mobile</option>
              <option value="WILL_CALL">Will call</option>
            </select>
          </Field>
          <Field label="Note" htmlFor="no-note"><input id="no-note" className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
        </div>
        {known.data ? (
          <div className="px-5 pb-4">
            <CustomerWarnings record={known.data.record} />
          </div>
        ) : null}
      </Panel>

      <Panel title="3. Payment" description={total ? `Order total ${dollars(total)}` : ''}>
        <div className="space-y-3 px-5 py-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-1"><input type="radio" checked={payment === 'FULL'} onChange={() => setPayment('FULL')} /> Paid in full</label>
            {can('orders.balance_due') ? (
              <>
                <label className="flex items-center gap-1"><input type="radio" checked={payment === 'DEPOSIT'} onChange={() => setPayment('DEPOSIT')} /> Deposit now, balance due</label>
                <label className="flex items-center gap-1"><input type="radio" checked={payment === 'NONE'} onChange={() => setPayment('NONE')} /> Nothing now, balance due</label>
              </>
            ) : null}
          </div>

          {payment !== 'NONE' ? (
            <div className="flex flex-wrap items-end gap-3">
              <Field label="Method" htmlFor="no-kind">
                <select id="no-kind" className={INPUT} value={kind} onChange={(e) => { setKind(e.target.value as Kind); setCard(null); }}>
                  <option value="CHECK">Check</option>
                  <option value="CARD_KEYED">Card (keyed)</option>
                  {can('box_office.house_account') ? <option value="HOUSE_ACCOUNT">House account</option> : null}
                </select>
              </Field>
              {payment === 'DEPOSIT' ? (
                <Field label="Deposit" htmlFor="no-deposit">
                  <input id="no-deposit" className={INPUT} value={deposit} onChange={(e) => { setDeposit(e.target.value); setCard(null); }} inputMode="decimal" />
                </Field>
              ) : null}
              {kind === 'CHECK' ? <Field label="Check number" htmlFor="no-check"><input id="no-check" className={INPUT} value={checkNumber} onChange={(e) => setCheckNumber(e.target.value)} /></Field> : null}
              {kind === 'HOUSE_ACCOUNT' ? <Field label="Organization id" htmlFor="no-org"><input id="no-org" className={INPUT} value={orgId} onChange={(e) => setOrgId(e.target.value)} /></Field> : null}
              {kind === 'CARD_KEYED' ? (card ? (card.ready ? <Badge tone="ok">card authorized for {dollars(card.amount_cents)}</Badge> : <Button onClick={() => void authorize()}>Authorize {dollars(card.amount_cents)}</Button>) : <Button onClick={() => void startCard()}>Enter card…</Button>) : null}
            </div>
          ) : null}
          {kind === 'CARD_KEYED' && card && payment !== 'NONE' ? <div id="new-order-card" className="max-w-md" /> : null}

          {payment !== 'FULL' ? (
            <div className="space-y-3 rounded border border-line bg-page p-3">
              <p className="text-sm text-ink-900">Balance due: <span className="tabular font-semibold">{dollars(Math.max(0, total - amountNow))}</span></p>
              <div className="flex flex-wrap items-end gap-3">
                <Field label="Due date" htmlFor="no-due"><input id="no-due" type="date" className={INPUT} value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></Field>
                <Field label="Tickets" htmlFor="no-issuance">
                  <select id="no-issuance" className={INPUT} value={issuance} onChange={(e) => setIssuance(e.target.value as IssuancePolicy)}>
                    <option value="ISSUE_WHEN_PAID">Issue when paid in full</option>
                    <option value="ISSUE_BUT_DISABLE_ADMISSION_UNTIL_PAID">Issue now, no entry until paid</option>
                    <option value="ISSUE_IMMEDIATELY">Issue now</option>
                  </select>
                </Field>
                <Field label="When past due" htmlFor="no-pastdue">
                  <select id="no-pastdue" className={INPUT} value={pastDue} onChange={(e) => setPastDue(e.target.value as PastDuePolicy)}>
                    <option value="NOTIFY_STAFF">Notify staff</option>
                    <option value="NO_AUTOMATIC_ACTION">No automatic action</option>
                    <option value="AUTO_CANCEL_AFTER_GRACE_PERIOD">Auto-cancel after grace period</option>
                  </select>
                </Field>
              </div>
              {can('orders.payment_requests.manage') ? (
                <>
                  <label className="flex items-center gap-2 text-sm text-ink-600">
                    <input type="checkbox" checked={sendLink} onChange={(e) => setSendLink(e.target.checked)} /> Email the customer a payment link
                  </label>
                  {sendLink ? <LinkFields value={link} onChange={setLink} balance={Math.max(0, total - amountNow)} /> : null}
                </>
              ) : null}
            </div>
          ) : null}

          {can('group_sales.manage') && total > 0 ? (
            <p className="text-sm text-ink-600">
              For a group that pays later?{' '}
              <button type="button" className="font-medium text-live hover:underline" onClick={() => navigate('/group-holds?new=1')}>
                Hold these seats for a group instead
              </button>
            </p>
          ) : null}
          <Button variant="primary" disabled={create.isPending || total <= 0} onClick={() => create.mutate()}>
            {create.isPending ? 'Creating…' : payment === 'FULL' ? `Create order · ${dollars(total)}` : `Create order · ${dollars(amountNow)} now, ${dollars(Math.max(0, total - amountNow))} due`}
          </Button>
        </div>
      </Panel>
    </div>
  );
}
